import { MessageDescriptor } from 'react-intl';

import { DocumentType, VersionStatus } from '@/entities/templates';

import { templateEditorMessages } from './i18n';
import type { ImportMode, ImportModeConfig, ImportPanelError } from './types';

export type SelectOption<V extends string = string> = {
  value: V;
  labelMessage: MessageDescriptor;
};

export const TEXT_IMPORT_EXTENSIONS = ['txt', 'md'];

export const IMPORT_PARSE_MODE_API_MAP = {
  blank_form: 'template',
  filled_record: 'sample',
} as const;

export const IMPORT_JSON_ACCEPT = { 'application/json': ['.json'] };

export const IMPORT_TEXT_ACCEPT = {
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
};

export const IMPORT_MODES: ImportMode[] = [
  'file',
  'blank_form',
  'filled_record',
];

export const IMPORT_MODE_CONFIG: Record<ImportMode, ImportModeConfig> = {
  file: {
    label: templateEditorMessages.importSegmentFile,
    dropHint: templateEditorMessages.importDropJson,
    action: templateEditorMessages.importButton,
    accept: IMPORT_JSON_ACCEPT,
  },
  blank_form: {
    label: templateEditorMessages.importSegmentBlank,
    placeholder: templateEditorMessages.importPlaceholderBlank,
    dropHint: templateEditorMessages.importDropText,
    action: templateEditorMessages.importAnalyse,
    accept: IMPORT_TEXT_ACCEPT,
  },
  filled_record: {
    label: templateEditorMessages.importSegmentFilled,
    placeholder: templateEditorMessages.importPlaceholderFilled,
    dropHint: templateEditorMessages.importDropText,
    action: templateEditorMessages.importAnalyse,
    accept: IMPORT_TEXT_ACCEPT,
  },
};

export const IMPORT_ERROR_MESSAGES: Record<
  ImportPanelError,
  MessageDescriptor
> = {
  noSections: templateEditorMessages.importNoSections,
  invalidJson: templateEditorMessages.importInvalidJson,
  parseFailed: templateEditorMessages.importError,
  unsupportedFile: templateEditorMessages.importUnsupportedFile,
  emptyFile: templateEditorMessages.importEmptyFile,
};

export const LANGUAGE_MESSAGES = {
  en: templateEditorMessages.languageEnglish,
  ru: templateEditorMessages.languageRussian,
  de: templateEditorMessages.languageGerman,
  es: templateEditorMessages.languageSpanish,
  pt: templateEditorMessages.languagePortuguese,
} as const;

export const GENERATION_MESSAGES = {
  always: templateEditorMessages.generationAlways,
  on_demand: templateEditorMessages.generationOnDemand,
} as const;

export const STATUS_OPTIONS: SelectOption<VersionStatus>[] = [
  { value: 'draft', labelMessage: templateEditorMessages.statusDraftLabel },
  { value: 'active', labelMessage: templateEditorMessages.statusCurrentLabel },
];

export const DOCUMENT_TYPE_OPTIONS: SelectOption<DocumentType>[] = [
  {
    value: 'EVALUATION',
    labelMessage: templateEditorMessages.docTypeEvaluation,
  },
  { value: 'SUMMARY', labelMessage: templateEditorMessages.docTypeSummary },
  { value: 'PROTOCOL', labelMessage: templateEditorMessages.docTypeProtocol },
  {
    value: 'LITERATURE_REVIEW',
    labelMessage: templateEditorMessages.docTypeLiteratureReview,
  },
  {
    value: 'FOLLOW_UP_LETTER',
    labelMessage: templateEditorMessages.docTypeFollowUpLetter,
  },
];

export const DOCUMENT_TYPE_DESCRIPTIONS: Record<
  DocumentType,
  MessageDescriptor
> = {
  PROTOCOL: templateEditorMessages.docTypeProtocolDescription,
  SUMMARY: templateEditorMessages.docTypeSummaryDescription,
  EVALUATION: templateEditorMessages.docTypeEvaluationDescription,
  FOLLOW_UP_LETTER: templateEditorMessages.docTypeFollowUpLetterDescription,
  LITERATURE_REVIEW: templateEditorMessages.docTypeLiteratureReviewDescription,
};

export const MIN_TEMPLATE_CODE_SLUG_LENGTH = 3;

export const AI_SLOT_PREFIX = '__ai_';

export const AI_OPTION_SLOT_NAME = `${AI_SLOT_PREFIX}content`;

export const PALETTE_DRAG_TYPES = {
  SECTION: 'palette-section',
  OPTION: 'palette-option',
  FIELD: 'palette-field',
} as const;

export const OPTION_DROP_ID_SEPARATOR = '-opt-';

export const EMPTY_SECTIONS_DROPZONE_ID = 'empty-sections-dropzone';
